// src/components/research/figures.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* Explanatory figures. Each shows a before and an after a reader can check
   against their own experience, which is the only reason any of them earns
   the space. None discloses a mechanism: they draw what is claimed to happen,
   never how it is computed.

   Two drawing conventions worth knowing before editing:

   1. Nodes are painted AFTER the lines that run into them and are filled with
      the page background, so a line drawn centre to centre is trimmed by the
      circle it arrives at. That is why the compression arc can start at a
      node's centre and still look like it starts at its edge.

   2. Arrowheads are markers with markerUnits="userSpaceOnUse" and orient=auto,
      so they stay one size regardless of the stroke that carries them and turn
      to follow the path. Marker ids are prefixed per figure because ids are
      document global and these figures share a page. */

const StyledFigureSet = styled.div`
  /* --set-max lets a figure sit narrower than the reading column. A three
     panel stack at full measure reads as a wall; pulled in, it reads as a
     diagram. */
  /* Doubled so it outranks the Figure wrapper's own "frame > * { max-width:
     100% }", which sits at the same specificity and was winning on source
     order. */
  && {
    max-width: var(--set-max, 100%);
  }
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--panel-basis, 150px), 1fr));
  gap: var(--space-6) var(--space-5);
  align-items: start;

  .panel {
    min-width: 0;
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  .step {
    display: block;
    text-align: center;
    margin-bottom: var(--space-2);
    color: var(--accent);
    font-family: var(--font-sans);
    font-size: var(--fz-xxs);
    font-weight: 600;
    letter-spacing: 0.12em;
  }

  .caption {
    margin: var(--space-3) auto 0;
    max-width: 30rem;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    line-height: var(--lh-body);
    text-align: center;
  }

  .faint {
    stroke: var(--border-strong);
    stroke-width: 1.5;
    fill: none;
  }

  .solid {
    stroke: var(--text-secondary);
    stroke-width: 1.75;
    fill: none;
  }

  .lit {
    stroke: var(--accent);
    stroke-width: 2;
    fill: none;
  }

  .node {
    fill: var(--bg);
    stroke: var(--text-secondary);
    stroke-width: 1.75;
  }

  .node-dim {
    fill: var(--bg);
    stroke: var(--border-strong);
    stroke-width: 1.5;
  }

  /* The piece that changes: muted, and never competing for attention. */
  .vary {
    fill: var(--border-strong);
    fill-opacity: 0.22;
    stroke: var(--border-strong);
    stroke-width: 1.25;
  }

  /* The piece that repeats: the accent, identical in every row. */
  .share {
    fill: var(--accent);
    fill-opacity: 0.28;
    stroke: var(--accent);
    stroke-width: 1.25;
  }

  text.tiny {
    font-size: 10px;
  }

  .bar {
    fill: var(--border-strong);
  }

  .bar-lit {
    fill: var(--accent);
  }

  /* Struck through a step that does not happen. Heavier than the arrow it
     cancels, because a thin cross reads as part of the drawing rather than as
     something laid over it. */
  .cross {
    stroke: var(--text-disabled);
    stroke-width: 2;
    stroke-linecap: round;
    fill: none;
  }

  /* A hypernode: a box rather than a circle, because these carry words. */
  .node-box {
    fill: var(--bg);
    stroke: var(--text-secondary);
    stroke-width: 1.5;
  }

  /* The same box, marked as the one thing in the drawing that was actually
     observed rather than inferred. */
  .node-lit {
    fill: var(--bg);
    stroke: var(--accent);
    stroke-width: 1.75;
  }

  /* The brace round a set of hypernodes. Dashed, because the set is not
     another object wrapping them — it is only which ones the edge names. */
  .set {
    fill: none;
    stroke: var(--accent);
    stroke-width: 1.25;
    stroke-dasharray: 4 4;
    opacity: 0.6;
  }

  text {
    fill: var(--text-secondary);
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
  }

  text.dim {
    fill: var(--text-disabled);
  }

  text.acc {
    fill: var(--accent);
  }

  text.small {
    font-size: 11px;
  }

  /* Type inside an SVG scales with the drawing, so a figure that is
     comfortable at 480px wide renders its labels at seven or eight pixels on a
     phone. The drawings themselves survive being small; the words do not. This
     enlarges the text relative to the geometry, and every figure has been
     checked at these sizes for collisions rather than just scaled up. */
  @media (max-width: 600px) {
    text {
      font-size: 15px;
    }

    text.small {
      font-size: 13px;
    }

    text.tiny {
      font-size: 12px;
    }
  }
`;

/* One arrow shape, three colours, at close to 2:1 so it reads as a point
   rather than a wedge. */
export const ARROW_LEN = 13;

const Arrowheads = ({ prefix }) => (
  <defs>
    {[
      ['lit', 'var(--accent)'],
      ['solid', 'var(--text-secondary)'],
      ['faint', 'var(--border-strong)'],
    ].map(([name, colour]) => (
      <marker
        key={name}
        id={`${prefix}-${name}`}
        viewBox="0 0 13 7"
        /* refX 0 puts the arrow's BASE on the path end, so the head sits
           entirely beyond the stroke and the stroke can never show through the
           tip. Paths are shortened by ARROW_LEN to compensate. The old refX of
           9.5 on a 10 unit marker placed the tip half a unit past the end
           instead, which is what made the line appear to spear the head. */
        refX="0"
        refY="3.5"
        markerWidth="13"
        markerHeight="7"
        orient="auto"
        markerUnits="userSpaceOnUse">
        <path d="M0 0 L13 3.5 L0 7 Z" fill={colour} />
      </marker>
    ))}
  </defs>
);

Arrowheads.propTypes = { prefix: PropTypes.string.isRequired };

/* Vertical centring for SVG labels, done once.
   ------------------------------------------------------------------------
   Every label in this file goes through <SvgLabel>. Nothing writes a bare <text>,
   and nothing hand-adjusts a y value, because that is what kept going wrong:
   a label was placed with `y={cy + 4}`, the 4 was right for one font size and
   one glyph, and the next label written a different way sat a pixel or two
   off inside its circle.

   The rule, and it is exact rather than eyeballed. Calibre's cap height
   measures 0.614em (canvas actualBoundingBoxAscent for a capital at a known
   size), so putting the alphabetic baseline 0.307em BELOW the target centre
   puts the middle of the cap band exactly on it. Expressed in em, so it holds
   at 10px and at 15px without anyone recomputing anything.

   Cap band rather than per-string ink, deliberately. Measured per string the
   ideal offset ranges from -0.013em for "y" to 0.17em for "excludes", so ink
   centring would set two labels in adjacent boxes at visibly different
   heights. Centring every label on the same band is what makes a row of them
   look level, which is the actual goal.

   dominant-baseline is not used: "central" is defined against the font's own
   ascent and descent, which are asymmetric here (0.65 / 0.35), so it lands
   above the cap band's centre and would need this same correction anyway on
   top of a property whose interpretation varies between engines. */
const CAP_HALF = '0.307em';

const SvgLabel = ({ x, y, anchor, className, transform, children }) => (
  <text x={x} y={y} dy={CAP_HALF} textAnchor={anchor} className={className} transform={transform}>
    {children}
  </text>
);

SvgLabel.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  anchor: PropTypes.oneOf(['start', 'middle', 'end']),
  className: PropTypes.string,
  transform: PropTypes.string,
  children: PropTypes.node.isRequired,
};

SvgLabel.defaultProps = { anchor: 'middle', className: undefined, transform: undefined };

const Panel = ({ step, note, children, viewBox, showNote }) => (
  <div className="panel">
    {step ? <span className="step">{step}</span> : null}
    <svg viewBox={viewBox} role="img" aria-label={note}>
      {children}
    </svg>
    {showNote ? <p className="caption">{note}</p> : null}
  </div>
);

Panel.propTypes = {
  step: PropTypes.string,
  note: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  viewBox: PropTypes.string,
  showNote: PropTypes.bool,
};

Panel.defaultProps = { viewBox: '0 0 392 92', step: null, showNote: true };

/* ── A. Path compression ───────────────────────────────────────────────── */

const NODES = [
  { x: 40, label: 'A' },
  { x: 146, label: 'B' },
  { x: 252, label: 'C' },
  { x: 358, label: 'D' },
];
const NODE_R = 19;
const NODE_Y = 34;

const chain = (cls, opacity = 1, marker = null) =>
  NODES.slice(0, -1).map((n, i) => (
    <path
      key={n.label}
      className={cls}
      d={`M${n.x + NODE_R + 3} ${NODE_Y} H${NODES[i + 1].x - NODE_R - 4 - ARROW_LEN}`}
      opacity={opacity}
      markerEnd={marker ? `url(${marker})` : undefined}
    />
  ));

// Painted last so every line arriving at a node is clipped by its fill.
const nodes = (dim = []) =>
  NODES.map(({ x, label }, i) => (
    <g key={label}>
      <circle cx={x} cy={NODE_Y} r={NODE_R} className={dim.includes(i) ? 'node-dim' : 'node'} />
      <SvgLabel x={x} y={NODE_Y} className={dim.includes(i) ? 'dim' : undefined}>
        {label}
      </SvgLabel>
    </g>
  ));

/* The bow starts at A's centre and is covered back to A's edge by A's fill.
   It stops just outside D so the arrowhead is not swallowed too. */
// Ends short of D by the arrow's length; the head completes the run.
const BOW = 'M40 34 Q196 114 326 49';

export const FigurePathCompression = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '30rem' }}>
    <Panel step="1" note="A path gets walked: A to B to C to D.">
      <Arrowheads prefix="pc1" />
      {chain('solid', 1, '#pc1-solid')}
      {nodes()}
    </Panel>

    <Panel
      step="2"
      note="Walked often enough, a route straight from A to D starts forming underneath it.">
      <Arrowheads prefix="pc2" />
      <path className="lit" d={BOW} strokeDasharray="6 6" opacity="0.8" markerEnd="url(#pc2-lit)" />
      {chain('faint', 1, '#pc2-faint')}
      {nodes()}
    </Panel>

    <Panel
      step="3"
      note="A reaches D on its own. B and C are still there and no longer on the way, which is why the steps cannot be recalled afterwards.">
      <Arrowheads prefix="pc3" />
      <path className="lit" d={BOW} markerEnd="url(#pc3-lit)" />
      {chain('faint', 0.35, '#pc3-faint')}
      {nodes([1, 2])}
    </Panel>
  </StyledFigureSet>
);

/* ── B. Self assembly ──────────────────────────────────────────────────────
   Four situations rather than two, because the argument is statistical: the
   shared part survives BECAUSE it recurs, and two examples do not read as
   recurrence.

   Colour does the separating. The repeated part is the accent and is literally
   the same shape in every row; the varying parts are muted and each carries
   its own letter, so a reader can see at a glance which piece is the same one
   over and over. Blocks rather than intersecting ellipses: a lens between two
   ellipses is a function of both radii, so it aligns with nothing. */

const SITUATIONS = ['a', 'b', 'c', 'd'];
const ROW_H = 22;
const ROW_GAP = 6;
const ROW_TOP = 14;
const VARY_W = 26;
const SHARE_W = 30;
const LEFT_X = 22;
const RIGHT_X = 112;

const rowY = i => ROW_TOP + i * (ROW_H + ROW_GAP);

// One state: the varying piece, then the repeated piece butted against it.
const stateBlock = (x, y, letter, { share = 'share', vary = 'vary' } = {}) => (
  <>
    <rect x={x} y={y} width={VARY_W} height={ROW_H} rx="3" className={vary} />
    <SvgLabel x={x + VARY_W / 2} y={y + ROW_H / 2} className="dim tiny">
      {letter}
    </SvgLabel>
    <rect x={x + VARY_W} y={y} width={SHARE_W} height={ROW_H} rx="3" className={share} />
  </>
);

const rows = opts =>
  SITUATIONS.map((letter, i) => (
    <g key={letter}>
      {stateBlock(LEFT_X, rowY(i), letter, opts)}
      {stateBlock(RIGHT_X, rowY(i), letter, opts)}
    </g>
  ));

const links = (cls, opacity, marker) =>
  SITUATIONS.map((letter, i) => (
    <path
      key={letter}
      className={cls}
      d={`M${LEFT_X + VARY_W + SHARE_W + 4} ${rowY(i) + ROW_H / 2} H${
        RIGHT_X - 4 - (marker ? ARROW_LEN : 0)
      }`}
      opacity={opacity}
      markerEnd={marker ? `url(${marker})` : undefined}
    />
  ));

const SA_BOX = '0 0 170 126';
// Vertical centre of the band the four rows occupy, so the survivor in panel 4
// sits where the stack was rather than at the top of it.
const SA_MID = (rowY(0) + rowY(SITUATIONS.length - 1) + ROW_H) / 2;

export const FigureSelfAssembly = () => (
  <StyledFigureSet style={{ '--panel-basis': '112px' }}>
    <Panel
      viewBox={SA_BOX}
      step="1"
      note="Four situations, each a state followed by another. Nothing here is a thing yet.">
      <Arrowheads prefix="sa1" />
      {rows({ share: 'vary' })}
      {links('faint', 1, '#sa1-faint')}
    </Panel>

    <Panel
      viewBox={SA_BOX}
      step="2"
      note="Part of them is the same every time. The rest changes from one to the next.">
      <Arrowheads prefix="sa2" />
      {rows()}
      {links('faint', 0.5, '#sa2-faint')}
    </Panel>

    <Panel
      viewBox={SA_BOX}
      step="3"
      note="So the link between the repeated parts is reinforced by all four, and each of the others by only one.">
      <Arrowheads prefix="sa3" />
      {rows()}
      {links('faint', 0.18, null)}
      <path
        className="lit"
        d={`M${LEFT_X + VARY_W + SHARE_W / 2} ${rowY(3) + ROW_H + 8} H${
          RIGHT_X + VARY_W + SHARE_W / 2 - ARROW_LEN
        }`}
        markerEnd="url(#sa3-lit)"
      />
    </Panel>

    {/* Same block, same size, same fill as in the panels before it. Drawn as an
        outline it read as a new object introduced at the end; drawn identically
        it reads as the one piece that was there the whole time and is now the
        only thing left. Centred on the band the four rows occupied. */}
    <Panel
      viewBox={SA_BOX}
      step="4"
      note="What is left standing is a region reliable enough to be treated as one thing.">
      <Arrowheads prefix="sa4" />
      <rect
        x={LEFT_X + VARY_W}
        y={SA_MID - ROW_H / 2}
        width={SHARE_W}
        height={ROW_H}
        rx="3"
        className="share"
      />
      <rect
        x={RIGHT_X + VARY_W}
        y={SA_MID - ROW_H / 2}
        width={SHARE_W}
        height={ROW_H}
        rx="3"
        className="share"
      />
      <path
        className="lit"
        d={`M${LEFT_X + VARY_W + SHARE_W + 4} ${SA_MID} H${RIGHT_X + VARY_W - 4 - ARROW_LEN}`}
        markerEnd="url(#sa4-lit)"
      />
    </Panel>
  </StyledFigureSet>
);

/* ── C. Filtering ──────────────────────────────────────────────────────────
   Each level is a stage of processing, and the width of a bar is how much is
   left to carry at that stage. A structure that can take the signal further
   down removes more entropy; a structure that cannot leaves the remainder as
   noise, which has to be held close to the way it arrived. */

const ROWS = [
  { y: 52, label: 'sound' },
  { y: 104, label: 'phonetics' },
  { y: 156, label: 'meaning' },
];
// Between the rows, naming the step rather than the level. Accent, like the
// arrows they belong to, so they never read as another stage label.
const STEPS = [
  { y: 78, label: 'sound to phonetics' },
  { y: 130, label: 'phonetics to meaning' },
];
const COL_A = 250;
const COL_B = 384;
const BAR_H = 14;
const LABEL_X = 150;
const AXIS_X = 30;

// Centred on the column axis, so every bar in a column shares one centre line.
const bar = (cx, y, w, cls = 'bar') => (
  <rect x={cx - w / 2} y={y - BAR_H / 2} width={w} height={BAR_H} rx={BAR_H / 2} className={cls} />
);

// Down the row it can reach, then on to the next.
const drop = (cx, from, to, marker) => (
  <path className="lit" d={`M${cx} ${from} V${to - ARROW_LEN}`} markerEnd={`url(${marker})`} />
);

export const FigureFiltering = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '30rem' }}>
    <Panel
      viewBox="0 0 448 214"
      showNote={false}
      note="The same sentence into two structures. Each step removes entropy; what cannot be reduced stays noise.">
      <Arrowheads prefix="fl" />

      {/* The axis carries one quantity and names both of its ends. */}
      <SvgLabel x={10} y={107} className="dim tiny" transform="rotate(-90 10 107)">
        entropy
      </SvgLabel>
      <path className="faint" d={`M${AXIS_X} 40 V172`} opacity="0.4" />
      <SvgLabel x={AXIS_X} y={26} className="dim tiny">
        high
      </SvgLabel>
      <SvgLabel x={AXIS_X} y={182} className="dim tiny">
        low
      </SvgLabel>

      {/* Stage labels and step labels share one right edge, so they read down
          as a ladder: level, step, level, step, level. */}
      {ROWS.map(({ y, label }) => (
        <SvgLabel key={label} x={LABEL_X} y={y} anchor="end" className="small">
          {label}
        </SvgLabel>
      ))}
      {STEPS.map(({ y, label }) => (
        <SvgLabel key={label} x={LABEL_X} y={y} anchor="end" className="acc tiny">
          {label}
        </SvgLabel>
      ))}

      <SvgLabel x={COL_A} y={22} className="acc small">
        Chinese
      </SvgLabel>
      <SvgLabel x={COL_B} y={22} className="dim small">
        Hindi
      </SvgLabel>

      {/* Reduced all the way down, so what is left to hold is small. */}
      {bar(COL_A, ROWS[0].y, 112)}
      {bar(COL_A, ROWS[1].y, 64)}
      {bar(COL_A, ROWS[2].y, 20, 'bar-lit')}
      {drop(COL_A, 64, 92, '#fl-lit')}
      {drop(COL_A, 116, 144, '#fl-lit')}
      <SvgLabel x={COL_A} y={196} className="acc tiny">
        less noise left to hold
      </SvgLabel>

      {/* Identical to the left column down to phonetics: the sounds are heard
          either way, and drawn the same way because they are the same thing.
          The whole difference is the step that does not happen, so that step is
          the only thing this column draws differently. */}
      {bar(COL_B, ROWS[0].y, 112)}
      {bar(COL_B, ROWS[1].y, 64)}
      {drop(COL_B, 64, 92, '#fl-lit')}

      {/* The step that cannot be taken: the same arrow as the two on the left,
          drawn in the muted stroke and struck out. An arrow with a cross on it
          says "this does not happen" in a way a line trailing off does not. */}
      <path className="faint" d={`M${COL_B} 116 V${144 - ARROW_LEN}`} markerEnd="url(#fl-faint)" />
      <path className="cross" d={`M${COL_B - 7} 123 L${COL_B + 7} 137`} />
      <path className="cross" d={`M${COL_B + 7} 123 L${COL_B - 7} 137`} />

      <SvgLabel x={COL_B} y={196} className="dim tiny">
        more noise left to hold
      </SvgLabel>
    </Panel>
  </StyledFigureSet>
);

/* ── D. Hypergraph ─────────────────────────────────────────────────────────
   Three panels that belong together, because all three draw the same object
   at the same moment in the argument: what a hyperedge is, and why the case a
   typed graph cannot express costs nothing here.

   The construction is drawn literally. A hyperedge is an ordered pair of SETS,
   so both ends are dashed set braces in panels 2 and 3, not a set on the left
   and a bare node on the right. Drawing the destination as a single node was
   the earlier version, and it quietly showed a different formalism: a set
   pointing at a thing.

   Hypernodes are boxes rather than circles because they carry words. */

const PILL_H = 26;

// The one hypernode shape. Everything that is a node in any figure below is
// drawn with this, so a node is the same object at the same size throughout.
const pill = (x, y, w, label, { cls = 'node-box', h = PILL_H } = {}) => (
  <>
    <rect x={x} y={y} width={w} height={h} rx={h / 2} className={cls} />
    <SvgLabel x={x + w / 2} y={y + h / 2} className="small">
      {label}
    </SvgLabel>
  </>
);

const HG_BOX = '0 0 340 118';
const HG_MID = 59;

/* A set of hypernodes: the brace, and the members inside it. Members stack,
   because a set has no order and a column reads less like a sequence than a
   row does. Height follows from the member count, so the brace can never be
   the wrong size for what is in it. */
const hgSet = (x, w, members) => {
  const inset = 10;
  const gap = 8;
  const boxH = members.length * PILL_H + (members.length - 1) * gap + inset * 2;
  const boxY = HG_MID - boxH / 2;

  return (
    <>
      <rect x={x} y={boxY} width={w} height={boxH} rx="12" className="set" />
      {members.map((label, i) => (
        <React.Fragment key={label}>
          {pill(x + inset, boxY + inset + i * (PILL_H + gap), w - inset * 2, label)}
        </React.Fragment>
      ))}
    </>
  );
};

// Identical in panels 2 and 3, which is the point those panels are making.
const hgEdge = prefix => (
  <path
    className="lit"
    d={`M182 ${HG_MID} H${236 - 6 - ARROW_LEN}`}
    markerEnd={`url(#${prefix}-lit)`}
  />
);

export const FigureHypergraph = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '28rem' }}>
    <Panel
      viewBox={HG_BOX}
      step="1"
      note="In a typed graph the relations are the edges, and an edge is not a thing. A relation between two relations has nothing to attach to.">
      <Arrowheads prefix="hg1" />

      {/* Labels sit clear of the curves they name. They used to sit at each
          curve's peak on a filled disc that existed only to hide the stroke
          running under the glyph, which read as a third node in a drawing that
          has two. */}
      <path className="solid" d="M98 54 Q170 14 238 50" markerEnd="url(#hg1-solid)" />
      <path className="solid" d="M98 64 Q170 104 238 68" markerEnd="url(#hg1-solid)" />
      <SvgLabel x={170} y={18} className="acc small">
        &gt;
      </SvgLabel>
      <SvgLabel x={170} y={100} className="acc small">
        &lt;
      </SvgLabel>

      {/* Joins nothing at either end, deliberately. The two edges are what the
          missing relation would have to hold between, and an edge has nowhere
          for it to land. */}
      <path className="faint" d="M170 42 V76" strokeDasharray="4 4" opacity="0.6" />
      <SvgLabel x={182} y={HG_MID} anchor="start" className="dim small">
        ?
      </SvgLabel>

      {pill(30, HG_MID - PILL_H / 2, 64, 'x')}
      {pill(246, HG_MID - PILL_H / 2, 64, 'y')}
    </Panel>

    <Panel
      viewBox={HG_BOX}
      step="2"
      note="A hyperedge is an ordered pair of sets of hypernodes. Making the relation R a hypernode puts it in the source set, so R holding from A to B is one edge.">
      <Arrowheads prefix="hg2" />
      {hgSet(20, 156, ['A', 'R'])}
      {hgEdge('hg2')}
      {hgSet(236, 84, ['B'])}
    </Panel>

    <Panel
      viewBox={HG_BOX}
      step="3"
      note="A relation between two relations is the identical drawing with different members. Nothing was added to the formalism to allow it.">
      <Arrowheads prefix="hg3" />
      {hgSet(20, 156, ['>', 'excludes'])}
      {hgEdge('hg3')}
      {hgSet(236, 84, ['<'])}
    </Panel>
  </StyledFigureSet>
);

/* ── E. The three kinds of inference ───────────────────────────────────────
   Three separate figures, each sitting with the paragraph it belongs to,
   because they are three separate claims rather than three stages of one.
   Grouped, a reader had to carry all three explanations to the one place the
   drawings were.

   They share a geometry, so reading down the section the same shapes stay in
   the same columns: a source set at a fixed x and width, one edge, a
   destination node. Every row is symmetric about INFER.cx, which is the centre of
   every viewBox here, so these are centred by construction rather than by
   inspection. */

const INFER = {
  cx: 170,
  setX: 32,
  setW: 164,
  destX: 236,
  destW: 72,
  inset: 10,
  gap: 8,
  pillH: 24,
  boxH: 40,
};

// Width the two members of a source set have to share between them.
const INFER_MEMBERS_W = INFER.setW - INFER.inset * 2 - INFER.gap;

/* One statement: the source set {member, relation}, the edge, the destination.
   `a` and `b` are [label, width] and their widths must sum to INFER_MEMBERS_W —
   asserted rather than trusted, because a mismatch shifts the row off centre
   by half the error and that is exactly the kind of drift nobody notices. */
const inferenceRow = (key, y, a, b, dest, { lit = false, sourceCls, destCls } = {}) => {
  if (a[1] + b[1] !== INFER_MEMBERS_W) {
    throw new Error(`inferenceRow "${key}": member widths must sum to ${INFER_MEMBERS_W}`);
  }

  const pillY = y - INFER.pillH / 2;
  const opts = { h: INFER.pillH };

  return (
    <React.Fragment key={key}>
      <rect
        x={INFER.setX}
        y={y - INFER.boxH / 2}
        width={INFER.setW}
        height={INFER.boxH}
        rx="12"
        className="set"
      />
      {pill(INFER.setX + INFER.inset, pillY, a[1], a[0], { ...opts, cls: sourceCls })}
      {pill(INFER.setX + INFER.inset + a[1] + INFER.gap, pillY, b[1], b[0], opts)}
      <path
        className={lit ? 'lit' : 'faint'}
        d={`M${INFER.setX + INFER.setW + 6} ${y} H${INFER.destX - 6 - ARROW_LEN}`}
        markerEnd={`url(#in-${lit ? 'lit' : 'faint'})`}
      />
      {pill(INFER.destX, pillY, INFER.destW, dest, { ...opts, cls: destCls })}
    </React.Fragment>
  );
};

export const FigureDeduction = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '24rem' }}>
    <Panel
      viewBox="0 0 340 196"
      showNote={false}
      note="Both premises written as hyperedges, and the conclusion as the edge that composing them reaches.">
      <Arrowheads prefix="in" />

      {inferenceRow('p1', 34, ['Socrates', 84], ['is a', 52], 'man')}
      {inferenceRow('p2', 96, ['man', 58], ['implies', 78], 'mortal')}

      {/* Premises above, conclusion below. */}
      <path
        className="faint"
        d={`M${INFER.setX} 130 H${INFER.destX + INFER.destW}`}
        opacity="0.8"
      />

      {inferenceRow('c', 162, ['Socrates', 84], ['is a', 52], 'mortal', { lit: true })}
      <SvgLabel x={216} y={146} className="acc tiny">
        therefore
      </SvgLabel>
    </Panel>
  </StyledFigureSet>
);

export const FigureInduction = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '24rem' }}>
    <Panel
      viewBox="0 0 340 232"
      showNote={false}
      note="Three cases carrying the same edge, and the hypernode induction forms so that the edge can be attached once instead of three times.">
      <Arrowheads prefix="in" />

      {[1, 2, 3].map((n, i) =>
        inferenceRow(`case${n}`, 26 + i * 46, [`swan ${n}`, 84], ['is', 52], 'white'),
      )}

      {/* Points at the new hypernode rather than at the edge: the hypernode is
          the part that did not exist before. */}
      <path className="lit" d="M84 144 V157" markerEnd="url(#in-lit)" />

      {inferenceRow('gen', 196, ['swan', 84], ['is', 52], 'white', {
        lit: true,
        sourceCls: 'node-lit',
      })}
    </Panel>
  </StyledFigureSet>
);

export const FigureAbduction = () => (
  <StyledFigureSet style={{ '--panel-basis': '100%', '--set-max': '24rem' }}>
    <Panel
      viewBox="0 0 340 130"
      showNote={false}
      note="The edge that exists, and the direction abduction reads it in.">
      <Arrowheads prefix="in" />

      {inferenceRow('e', 34, ['rain', 60], ['causes', 76], 'wet ground', { destCls: 'node-lit' })}
      <SvgLabel x={272} y={12} className="acc tiny">
        seen
      </SvgLabel>

      <path
        className="lit"
        d="M290 50 Q184 108 78 50"
        strokeDasharray="6 5"
        markerEnd="url(#in-lit)"
      />
      <SvgLabel x={184} y={104} className="acc tiny">
        what would explain it
      </SvgLabel>
    </Panel>
  </StyledFigureSet>
);

/* ── F. Direction of the search ────────────────────────────────────────────
   The one figure in "The Problem", and the only one on the page that draws a
   strategy rather than a mechanism. Two panels, one origin, one destination.

   The destination is the same object in both panels and is drawn in the same
   place: what changes between them is only whether it is described. That is
   the whole argument, so nothing else may differ between the panels. */

const SD_BOX = '0 0 200 132';
const SD_ORIGIN = { x: 26, y: 66 };
const SD_TARGET = { x: 158, y: 60 };
const SD_TARGET_R = 25;
const SD_NODE_R = 7;

/* Six sampled routes, fanned about the horizontal. Endpoints are computed
   rather than written out, so the fan stays symmetric if the spread changes. */
const SD_ANGLES = [-42, -25, -8, 8, 25, 42];
const SD_REACH = 66;

const searchRay = deg => {
  const rad = (deg * Math.PI) / 180;
  const at = r => ({
    x: SD_ORIGIN.x + r * Math.cos(rad),
    // Negated: screen y runs downwards, so a positive angle has to rise.
    y: SD_ORIGIN.y - r * Math.sin(rad),
  });
  const from = at(SD_NODE_R + 3);
  // Stops short by the arrow's length; the head completes the run.
  const to = at(SD_REACH - ARROW_LEN);
  return `M${from.x.toFixed(1)} ${from.y.toFixed(1)} L${to.x.toFixed(1)} ${to.y.toFixed(1)}`;
};

const searchOrigin = () => (
  <circle cx={SD_ORIGIN.x} cy={SD_ORIGIN.y} r={SD_NODE_R} className="node" />
);

/* Two lines, not one. Set on one line the label is about 117 user units wide at
   the small size this file switches to below 600px, which overruns the 200 unit
   viewBox and is clipped by the figure frame's overflow. Splitting it also keeps
   it clear of the widest sampled route in the left panel. */
const sdTargetLabel = tone =>
  ['human', 'intelligence'].map((word, i) => (
    <SvgLabel
      key={word}
      x={SD_TARGET.x}
      y={SD_TARGET.y + SD_TARGET_R + 13 + i * 14}
      className={`${tone} small`}>
      {word}
    </SvgLabel>
  ));

export const FigureSearchDirection = () => (
  <StyledFigureSet style={{ '--panel-basis': '150px', '--set-max': '34rem' }}>
    <Panel
      viewBox={SD_BOX}
      step="Current approaches"
      note="Routes are sampled outward and tested one at a time. The destination is never described, so no route can be reasoned about before it is tried.">
      <Arrowheads prefix="sd1" />
      {SD_ANGLES.map(a => (
        <path key={a} className="faint" d={searchRay(a)} markerEnd="url(#sd1-faint)" />
      ))}
      {/* Present, but not described: the dashes say the boundary is a guess. */}
      <circle
        cx={SD_TARGET.x}
        cy={SD_TARGET.y}
        r={SD_TARGET_R}
        className="set"
        strokeDasharray="5 5"
      />
      <SvgLabel x={SD_TARGET.x} y={SD_TARGET.y} className="dim">
        ?
      </SvgLabel>
      {searchOrigin()}
      {sdTargetLabel('dim')}
    </Panel>

    <Panel
      viewBox={SD_BOX}
      step="This work"
      note="Describe the destination first. Only then is there anything to reason backwards from about which route could reach it.">
      <Arrowheads prefix="sd2" />
      {/* Dashed, because this is the direction of the reasoning and not a route
          anyone travels. */}
      <path
        className="lit"
        d={`M${SD_TARGET.x - SD_TARGET_R - 4} ${SD_ORIGIN.y} H${
          SD_ORIGIN.x + SD_NODE_R + 4 + ARROW_LEN
        }`}
        strokeDasharray="6 6"
        markerEnd="url(#sd2-lit)"
      />
      <circle cx={SD_TARGET.x} cy={SD_TARGET.y} r={SD_TARGET_R} className="node-lit" />
      {searchOrigin()}
      {sdTargetLabel('acc')}
    </Panel>
  </StyledFigureSet>
);
